/**
 * Employee Onboarding Component - Peer-to-peer onboarding via QR code
 *
 * This component allows existing employees to create onboarding payloads
 * for new employees (QR code based, like Signal).
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import QRCode from 'qrcode';
import {
  useCryptoKeyStore,
  OnboardingPayload,
} from '@antragsmanager/crypto-lib';
import { onboardEmployee } from '../../api/keysApi';

interface EmployeeOnboardingProps {
  open: boolean;
  onClose: () => void;
  authorityId: string;
  authorityName: string;
  onComplete?: () => void;
}

type TabValue = 'create' | 'scan';

export function EmployeeOnboarding({
  open,
  onClose,
  authorityId,
  authorityName,
  onComplete,
}: EmployeeOnboardingProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('create');
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [newEmployeePublicKey, setNewEmployeePublicKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [onboardingPayload, setOnboardingPayload] = useState<OnboardingPayload | null>(null);
  const [success, setSuccess] = useState(false);

  // State for scan/import tab
  const [importPayloadText, setImportPayloadText] = useState('');
  const [importSuccess, setImportSuccess] = useState(false);

  const { activeKey, createOnboarding, importOnboarding } = useCryptoKeyStore();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setNewEmployeeName('');
      setNewEmployeePublicKey('');
      setError(null);
      setQrCodeDataUrl(null);
      setOnboardingPayload(null);
      setSuccess(false);
      setImportPayloadText('');
      setImportSuccess(false);
    }
  }, [open]);

  const handleCreateOnboarding = useCallback(async () => {
    if (!activeKey) {
      setError('Kein aktiver Schlüssel vorhanden');
      return;
    }

    if (!newEmployeeName.trim()) {
      setError('Bitte geben Sie den Namen des neuen Mitarbeiters ein');
      return;
    }

    if (!newEmployeePublicKey.trim()) {
      setError('Bitte geben Sie den Public Key des neuen Mitarbeiters ein');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Parse the public key
      let publicKeyJwk: JsonWebKey;
      let extractedKeyId: string | undefined;
      try {
        const parsed = JSON.parse(newEmployeePublicKey);
        // Handle both formats: full key export or just the public key
        publicKeyJwk = parsed.pub || parsed.publicKeyJwk || parsed;
        // Extract the key ID from the full export format if available
        extractedKeyId = parsed.id as string | undefined;
      } catch {
        setError('Ungültiges Public Key Format. Bitte verwenden Sie das JSON-Format.');
        setIsLoading(false);
        return;
      }

      // Create the onboarding payload
      const payload = await createOnboarding(authorityId, publicKeyJwk);
      setOnboardingPayload(payload);

      // Generate QR code
      const payloadJson = JSON.stringify(payload);
      const qrUrl = await QRCode.toDataURL(payloadJson, {
        errorCorrectionLevel: 'M',
        width: 300,
      });
      setQrCodeDataUrl(qrUrl);

      // Register the employee with the backend
      // Use extracted key ID from export, or kid from JWK, or generate new one
      const keyId = extractedKeyId ||
                   (publicKeyJwk as Record<string, unknown>).kid as string ||
                   crypto.randomUUID();

      // Calculate fingerprint (simplified - in production use proper fingerprint calculation)
      const fingerprintData = JSON.stringify(publicKeyJwk);
      const hashBuffer = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(fingerprintData)
      );
      const fingerprint = Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      await onboardEmployee(authorityId, {
        employeeKeyId: keyId,
        publicKeyJwk: publicKeyJwk,
        fingerprint: fingerprint,
        name: newEmployeeName.trim(),
        onboardedByKeyId: activeKey.keyId,
      });

      setSuccess(true);
    } catch (err) {
      console.error('Failed to create onboarding:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Fehler beim Erstellen des Onboarding. Bitte versuchen Sie es erneut.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [activeKey, newEmployeeName, newEmployeePublicKey, authorityId, createOnboarding]);

  const handleCopyPayload = useCallback(() => {
    if (onboardingPayload) {
      navigator.clipboard.writeText(JSON.stringify(onboardingPayload));
    }
  }, [onboardingPayload]);

  const handleImportOnboarding = useCallback(async () => {
    if (!activeKey) {
      setError('Kein aktiver Schlüssel vorhanden. Bitte erstellen Sie zuerst einen Schlüssel.');
      return;
    }

    if (!importPayloadText.trim()) {
      setError('Bitte fügen Sie die Onboarding-Payload ein');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Parse the payload
      let payload: OnboardingPayload;
      try {
        payload = JSON.parse(importPayloadText.trim());
      } catch {
        setError('Ungültiges Payload-Format. Bitte verwenden Sie das JSON-Format.');
        setIsLoading(false);
        return;
      }

      // Validate payload structure
      if (!payload.authorityId || !payload.wrappedBundle || !payload.epoch) {
        setError('Ungültige Onboarding-Payload. Erforderliche Felder fehlen.');
        setIsLoading(false);
        return;
      }

      // Import the onboarding payload
      await importOnboarding(payload);

      setImportSuccess(true);
    } catch (err) {
      console.error('Failed to import onboarding:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Fehler beim Importieren des Onboarding. Bitte versuchen Sie es erneut.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [activeKey, importPayloadText, importOnboarding]);

  const handleClose = () => {
    if (success && onComplete) {
      onComplete();
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mitarbeiter einladen - {authorityName}</DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value as TabValue)}
          sx={{ mb: 2 }}
        >
          <Tab label="Einladung erstellen" value="create" />
          <Tab label="Einladung annehmen" value="scan" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Mitarbeiter wurde erfolgreich eingeladen! Der neue Mitarbeiter kann den QR-Code
            oder die Payload verwenden, um sich anzumelden.
          </Alert>
        )}

        {activeTab === 'create' && (
          <Box>
            {!qrCodeDataUrl ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Geben Sie die Daten des neuen Mitarbeiters ein. Der neue Mitarbeiter muss
                  Ihnen seinen Public Key mitteilen (aus seiner Schlüsselsicherung).
                </Typography>

                <TextField
                  fullWidth
                  label="Name des neuen Mitarbeiters"
                  value={newEmployeeName}
                  onChange={(e) => setNewEmployeeName(e.target.value)}
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="Public Key (JSON)"
                  value={newEmployeePublicKey}
                  onChange={(e) => setNewEmployeePublicKey(e.target.value)}
                  margin="normal"
                  multiline
                  rows={4}
                  placeholder='{"kty":"EC","crv":"P-256","x":"...","y":"..."}'
                  helperText="Der neue Mitarbeiter muss seinen Public Key aus seiner Schlüsselsicherung exportieren"
                />
              </>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" gutterBottom>
                  QR-Code für {newEmployeeName}
                </Typography>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    display: 'inline-block',
                    my: 2,
                    bgcolor: 'white',
                  }}
                >
                  <img src={qrCodeDataUrl} alt="Onboarding QR Code" />
                </Paper>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Der neue Mitarbeiter kann diesen QR-Code mit der App scannen, um sich
                  anzumelden und Zugriff auf verschlüsselte Daten zu erhalten.
                </Typography>
                <Button variant="outlined" onClick={handleCopyPayload} sx={{ mt: 1 }}>
                  Payload kopieren
                </Button>
                {/* Hidden element for E2E testing - contains the payload JSON */}
                <input
                  type="hidden"
                  data-testid="onboarding-payload-json"
                  value={onboardingPayload ? JSON.stringify(onboardingPayload) : ''}
                />
              </Box>
            )}
          </Box>
        )}

        {activeTab === 'scan' && (
          <Box>
            {importSuccess ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Onboarding erfolgreich! Sie haben jetzt Zugriff auf die verschlüsselten Daten
                dieser Behörde.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Fügen Sie die Onboarding-Payload ein, die Sie von einem bestehenden Mitarbeiter
                  erhalten haben. Dies kann aus dem QR-Code oder durch Kopieren der Payload erfolgen.
                </Typography>

                <TextField
                  fullWidth
                  label="Onboarding-Payload (JSON)"
                  value={importPayloadText}
                  onChange={(e) => setImportPayloadText(e.target.value)}
                  margin="normal"
                  multiline
                  rows={6}
                  placeholder='{"authorityId":"...","wrappedBundle":"...","epoch":1,...}'
                  helperText="Die Payload enthält den verschlüsselten Zugangsschlüssel für die Behörde"
                  data-testid="onboarding-payload-input"
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {success || importSuccess ? 'Schließen' : 'Abbrechen'}
        </Button>
        {!qrCodeDataUrl && activeTab === 'create' && (
          <Button
            variant="contained"
            onClick={handleCreateOnboarding}
            disabled={isLoading || !newEmployeeName.trim() || !newEmployeePublicKey.trim()}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Einladung erstellen'}
          </Button>
        )}
        {activeTab === 'scan' && !importSuccess && (
          <Button
            variant="contained"
            onClick={handleImportOnboarding}
            disabled={isLoading || !importPayloadText.trim()}
            data-testid="process-onboarding-button"
          >
            {isLoading ? <CircularProgress size={24} /> : 'Onboarding verarbeiten'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
